import { Thresholds } from "@src/types/dashboards";
import store from "@src/reducers/store";
import { formatDateTimeForGraphLabel } from "@src/cloud-connect/utils/format-date-time-for-graph-label";
import { formatChartTooltipDate } from "@src/cloud-connect/utils/timezone-display-utils";
import { buildTooltipDateHeader } from "@src/cloud-connect/charts/chart-tooltip-utils";
import type { ChartThemeColors } from "../use-theme-colors-for-charts";
import { parseManualBounds } from "../axis-bounds";

/** Threshold colors per spec (§4.5): warning dotted laserOrange, critical dashed alarmRed. */
export const WARNING_THRESHOLD_COLOR = "#fd7f38"; // laserOrange
export const CRITICAL_THRESHOLD_COLOR = "#fd3838"; // alarmRed

/** App default tint for the line + marker + tooltip bullet. Picker offers a
 *  palette sourced from /src/scss/colors.module.scss; user None = this. */
export const DEFAULT_LINE_COLOR = "#027680";

export interface BuildTrendLineOptionArgs {
  times: string[];
  values: (number | null)[];
  /** Aggregated: per-bucket minimums (parallel to `times`/`values`).
   *  When provided AND `showMin` is true, draws a dashed lower-bound
   *  line at lighter opacity of the main color. */
  minValues?: (number | null)[];
  /** Aggregated: per-bucket maximums. Mirror of minValues for showMax. */
  maxValues?: (number | null)[];
  /** Manual Y bounds (engineering units). Applied only when both are
   *  present, finite and ordered; otherwise the automatic range is used. */
  yMin?: number | null;
  yMax?: number | null;
  /** When true, render the v_min sub-series (only meaningful when
   *  minValues is also provided). Driven by semantics.show_min. */
  showMin?: boolean;
  /** Drive semantics.show_max — render v_max sub-series. */
  showMax?: boolean;
  nodeName: string;
  breadcrumbs?: string;
  unitLabel?: string;
  thresholds?: Thresholds | null;
  /** Override tint for the line + marker + tooltip marker dot. */
  lineColor?: string;
  /** Static/read-only rendering (picker preview or unbound widget): no tooltip, no hover. */
  static?: boolean;
  /** Resolved theme colours from `useThemeColorsForCharts`. Drives
   *  legend, axis labels, and tooltip text. Omitted options fall
   *  back to the pre-picker baseline (`#e6f1f2` primary,
   *  `#697477` secondary), so the helper is safe to call from tests
   *  or any caller that hasn't set up the picker. */
  themeColors?: ChartThemeColors;
}

/** 1/2/5×10ⁿ "nice" step — the same tick family e-charts prefers. */
const niceStep = (range: number): number => {
  if (range <= 0 || !Number.isFinite(range)) return 1;
  const pow = 10 ** Math.floor(Math.log10(range));
  const frac = range / pow;
  const niceFrac = frac <= 1 ? 1 : frac <= 2 ? 2 : frac <= 5 ? 5 : 10;
  return niceFrac * pow;
};

/**
 * Y-axis range covering the series AND enabled thresholds, with a 10% margin,
 * snapped to a "nice" step — same style as the existing calculateValuesInterval
 * but doesn't touch it (that helper is used by another graph). Snapping matters:
 * an explicit max that doesn't land on the tick grid (e.g. threshold 50 + 10%
 * margin → 51) leaves the top border labeled with a non-grid value while the
 * other ticks stay clean.
 *
 * Aggregated bounds participate in the range calculation too: when the user
 * has flipped show_min / show_max on, the rendered sub-series should fit
 * inside the y-axis. We pass them in as separate arrays so the caller
 * decides whether they're displayable (raw mode ignores them).
 */
export const calculateTrendLineInterval = (
  values: (number | null)[],
  thresholds?: Thresholds | null,
  extraValues?: Array<(number | null)[] | undefined>,
): { yAxisMin: number; yAxisMax: number } => {
  const nums = values.filter((v): v is number => v != null);
  const thresholdNums = [thresholds?.warning, thresholds?.critical].filter(
    (v): v is number => v != null,
  );
  const extraNums = (extraValues ?? []).flatMap((arr) =>
    (arr ?? []).filter((v): v is number => v != null),
  );
  const all = [...nums, ...thresholdNums, ...extraNums];
  if (all.length === 0) {
    return { yAxisMin: 0, yAxisMax: 1 };
  }
  const minValue = Math.min(...all);
  const maxValue = Math.max(...all);
  const range = maxValue - minValue || 1;
  const margin = 0.1 * range;
  const rawMin = minValue - margin;
  const rawMax = maxValue + margin;
  // Snip the boundaries to multiples of a nice step (~5 ticks); the epsilon
  // absorbs float division noise so floor/ceil land on the exact step.
  const step = niceStep((rawMax - rawMin) / 5);
  const eps = 1e-9;
  // Both bounds are multiples of `step`, so rounding to the step's decimals
  // only strips float noise (194 * 0.1 is 19.400000000000002). Echarts rounds
  // its own snapped extent to the tick precision, which lands just below a
  // noisy min (19.4) and trips its dev-only consistency assert.
  const stepDecimals = Math.min(
    100,
    Math.max(0, -Math.floor(Math.log10(step))),
  );
  const snap = (value: number) => Number(value.toFixed(stepDecimals));
  const yAxisMin = snap(Math.floor(rawMin / step + eps) * step);
  let yAxisMax = snap(Math.ceil(rawMax / step - eps) * step);
  if (yAxisMin === yAxisMax) yAxisMax = snap(yAxisMin + step);
  return { yAxisMin, yAxisMax };
};

/**
 * Tooltip formatter: timestamp header + one row (marker + node name + breadcrumbs
 * + value + unit). Mirrors the app's existing chart tooltips.
 */
const buildTooltipFormatter =
  (
    nodeName: string,
    breadcrumbs: string | undefined,
    unitLabel: string,
    markerColor: string,
    tooltipTextColor: string,
  ) =>
  (params: any) => {
    const first = Array.isArray(params) ? params[0] : params;
    const { timezone, language } = store.getState().yourSlice;
    const formattedDate = formatChartTooltipDate(
      first.axisValue,
      timezone,
      language,
    );
    const value = first.data != null ? first.data : "—";

    // Cap every tooltip row to 160px so the box can't grow past where
    // echarts' `confine` would still let it overflow the chart canvas.
    let html = `<div style="max-width: 160px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${buildTooltipDateHeader(
      formattedDate,
    )}</div>`;
    html += `<div style="display: flex; gap: 5px; align-items: center; color: ${tooltipTextColor}; font-weight: 100;">
      <span style="color: ${markerColor};">●</span>
      <span style="display: inline-block; max-width: 160px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; vertical-align: middle;">${nodeName}</span>
    </div>`;
    if (breadcrumbs) {
      html += `<div style="padding-left: 15px; color: ${tooltipTextColor}; font-size: 10px; max-width: 160px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${breadcrumbs}</div>`;
    }
    html += `<div style="color: ${tooltipTextColor}; font-weight: 800; padding-top: 2px; max-width: 160px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${value}${
      unitLabel ? ` ${unitLabel}` : ""
    }</div>`;
    return html;
  };

export const buildTrendLineOption = ({
  times,
  values,
  minValues,
  maxValues,
  yMin,
  yMax,
  showMin = false,
  showMax = false,
  nodeName,
  breadcrumbs,
  unitLabel = "",
  thresholds,
  lineColor,
  static: isStatic = false,
  themeColors,
}: BuildTrendLineOptionArgs): any => {
  // The min/max sub-series (when both arrays + flags are present) get
  // factored into the y-axis range so they stay inside the chart bounds.
  const extras: Array<(number | null)[] | undefined> = [];
  if (showMin && minValues) extras.push(minValues);
  if (showMax && maxValues) extras.push(maxValues);
  const manual = parseManualBounds(yMin, yMax);
  // Only compute the automatic range when no manual pair is set.
  const { yAxisMin, yAxisMax } = manual
    ? { yAxisMin: manual.min, yAxisMax: manual.max }
    : calculateTrendLineInterval(values, thresholds, extras);
  // Single source of truth for line + marker + tooltip marker dot: the user's
  // chosen hex, or the app default (#027680 — scss $green).
  const color = lineColor ?? DEFAULT_LINE_COLOR;
  // Resolved theme colours (legend/axes/tooltip text). Caller
  // resolves via `useThemeColorsForCharts`; absent when the helper
  // runs in a test or on a page that never mounted the picker, so
  // we fall back to the pre-picker baseline.
  const primaryText = themeColors?.primary ?? "#e6f1f2";
  const secondaryText = themeColors?.secondary ?? "#697477";

  const markLineData = [
    ...(thresholds?.warning != null
      ? [{ yAxis: thresholds.warning, name: "Warning" }]
      : []),
    ...(thresholds?.critical != null
      ? [{ yAxis: thresholds.critical, name: "Critical" }]
      : []),
  ];

  return {
    // Legend top-left: series icon (auto-colored) + node name — the bottom
    // legend is gone. Reserves 16px under the legend row (only while the
    // legend is shown) + breathing space on the right so the last x-axis
    // timestamp isn't clipped.
    grid: {
      left: 8,
      right: 28,
      top: nodeName !== "" ? 32 : 8,
      bottom: 8,
      containLabel: true,
    },
    legend: {
      // Hidden when the series has no name to show (mocked/unbound tile) —
      // an unnamed chip is worse than no legend.
      show: nodeName !== "",
      // Static: legend is display-only — clicking must not hide a series.
      selectedMode: isStatic ? false : true,
      top: 0,
      left: 8,
      itemWidth: 16,
      itemHeight: 12,
      padding: [2, 8, 2, 0],
      textStyle: {
        color: primaryText,
        fontSize: 9,
        // Crop long node names with an ellipsis instead of overflowing.
        overflow: "truncate",
        width: 140,
      },
      // De-dupe: e-charts appends "(1)" to duplicate series names.
      formatter: (name: string) => name.replace(/\s\(\d+\)$/, ""),
    },
    xAxis: {
      type: "category",
      data: times,
      boundaryGap: false,
      axisLabel: {
        color: secondaryText,
        formatter: formatDateTimeForGraphLabel,
        fontSize: 9,
        // Explicit 10px between the chart's bottom tick line and the x-axis
        // timestamp labels.
        margin: 10,
      },
    },
    yAxis: {
      type: "value",
      min: yAxisMin,
      max: yAxisMax,
      scale: true,
      axisLine: { onZero: true },
      splitLine: { lineStyle: { color: "rgba(255,255,255,0.06)" } },
      // Fixed-width, right-aligned label column: with containLabel this makes
      // every chart reserve the same left gutter, so plots stay left-aligned
      // across widgets no matter how wide the numbers are (199.9-style labels
      // would otherwise push the plot right vs -0.1-style ones).
      axisLabel: {
        color: secondaryText,
        width: 44,
        align: "right",
        overflow: "truncate",
      },
    },
    tooltip: {
      show: !isStatic,
      trigger: "axis",
      triggerOn: !isStatic ? "mousemove|click" : "none", // static: never fires
      confine: true,
      // Half-transparent dark bg + light text: the formatter emits white rows,
      // so the tooltip must not use echarts' default light background (white-
      // on-white would make the popover invisible). 0.5 alpha lets chart
      // gridlines show through on dense widgets.
      backgroundColor: "rgba(19, 27, 32, 0.5)",
      borderColor: "rgba(230, 241, 242, 0.2)",
      borderWidth: 1,
      textStyle: { color: primaryText, fontFamily: "Inter", fontSize: 11 },
      formatter: buildTooltipFormatter(
        nodeName,
        breadcrumbs,
        unitLabel,
        color,
        primaryText,
      ),
      extraCssText:
        "backdrop-filter: blur(5px); padding: 8px 12px; box-sizing: border-box;",
    },
    series: [
      {
        name: nodeName,
        type: "line",
        data: values,
        smooth: false,
        lineStyle: { color, width: 2 },
        symbol: "circle",
        symbolSize: 6,
        showSymbol: values.length === 1,
        itemStyle: { color },
        connectNulls: true,
        animation: true,
        silent: isStatic, // kills hover emphasis too, not just the tooltip
        ...(markLineData.length > 0
          ? {
              // Warning + critical as markLine (per spec): dotted orange / dashed red.
              markLine: {
                silent: true,
                symbol: "none",
                label: { show: false },
                animation: false,
                data: markLineData.map((m) => ({
                  yAxis: m.yAxis,
                  lineStyle: {
                    color:
                      m.name === "Critical"
                        ? CRITICAL_THRESHOLD_COLOR
                        : WARNING_THRESHOLD_COLOR,
                    type: m.name === "Critical" ? "dashed" : "dotted",
                    width: 1.5,
                  },
                })),
              },
            }
          : {}),
      },
      // Aggregated bounds sub-series. Drawn AFTER the main avg line so
      // they paint on top in echarts painter order. Own tooltip rows so
      // hovering reveals the bound values; lighter opacity than the main
      // line keeps them readable without competing visually.
      ...(showMin && minValues
        ? [
            {
              name: `${nodeName} (min)`,
              type: "line",
              data: minValues,
              smooth: false,
              lineStyle: { color, width: 1.5, type: "dashed", opacity: 0.6 },
              symbol: "none",
              itemStyle: { color, opacity: 0.6 },
              connectNulls: true,
              animation: true,
              silent: isStatic,
            },
          ]
        : []),
      ...(showMax && maxValues
        ? [
            {
              name: `${nodeName} (max)`,
              type: "line",
              data: maxValues,
              smooth: false,
              lineStyle: { color, width: 1.5, type: "dashed", opacity: 0.6 },
              symbol: "none",
              itemStyle: { color, opacity: 0.6 },
              connectNulls: true,
              animation: true,
              silent: isStatic,
            },
          ]
        : []),
    ],
  };
};
